import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { theme } from '../theme';
import { navigationItems } from '../data/navigation';

// Animation de glissement de droite à gauche
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToLeft = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

// Animation du logo Outchou - seulement le scale, le translate reste fixe
const logoPulse = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
`;

const HeroSection = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  min-height: 600px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 85vh;
    min-height: 85vh;
  }

  @media (max-width: 480px) {
    height: 85vh;
    min-height: 85vh;
  }
`;

const SlideContainer = styled(motion.div)<{ $backgroundImage?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${props => props.$backgroundImage 
    ? `url(${props.$backgroundImage}) center/cover` 
    : theme.colors.outchou_blue};
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  will-change: transform;
  backface-visibility: hidden;
`;

const HeroOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: radial-gradient(63.99% 192.16% at 82.64% 42%, 
    rgba(54, 52, 55, 0.25) 0%, 
    rgba(34, 33, 35, 0.432543) 0%, 
    rgba(0, 0, 0, 0.49) 100%
  );
  z-index: 1;
`;

const VideoElement = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const LogoAnimation = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: none;
  animation: ${props => props.$show ? logoPulse : 'none'} 1.5s ease-in-out forwards;
  transform-origin: center center;
  transition: opacity 0.3s ease;
`;

const LogoWhite = styled.div`
  width: 120px;
  height: 120px;
  background: url('/shared_assets/images/logo_blanc.png') center/contain no-repeat;
  filter: brightness(0) invert(1);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
  }
`;

const MobileHeroLogo = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 40px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 80px;
    background: url('/shared_assets/images/logo_blanc.png') center/contain no-repeat;
    z-index: 5;
    cursor: pointer;
    transition: transform 0.2s ease;
    
    &:active {
      transform: translateX(-50%) scale(0.95);
    }
  }

  @media (max-width: 480px) {
    top: 30px;
    width: 70px;
    height: 70px;
  }
`;

const ScrollIndicator = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: absolute;
    bottom: 56px;
    left: 50%;
    transform: translateX(-50%);
    cursor: pointer;
    z-index: 5;
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    50% {
      transform: translateX(-50%) translateY(-10px);
    }
  }

  @media (max-width: 480px) {
    bottom: 8px;
  }
`;

const DoubleArrow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  
  &::before,
  &::after {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid ${theme.colors.white};
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
  
  &::before {
    animation: bounceArrow 2s infinite;
  }
  
  &::after {
    animation: bounceArrow 2s infinite 0.2s;
  }

  @keyframes bounceArrow {
    0%, 100% {
      opacity: 1;
      transform: rotate(45deg);
      translate: 0 0;
    }
    50% {
      opacity: 0.6;
      transform: rotate(45deg);
      translate: 0 -8px;
    }
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  z-index: 2;
  padding: 0 5%;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    top: auto;
    bottom: 116px;
    left: 0;
    padding: 0 5%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    bottom: 96px;
    padding: 0 4%;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.white};
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const HeroText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: ${theme.colors.white};
  margin: 0 0 16px 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 18px;
    margin-bottom: 8px;
  }
`;

const ClickableTextArea = styled.div<{ $isMobile: boolean }>`
  cursor: ${props => props.$isMobile ? 'pointer' : 'default'};
  
  @media (max-width: 768px) {
    &:active {
      opacity: 0.8;
    }
  }
`;

const DiscoverLink = styled.a`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 18px;
  color: ${theme.colors.white};
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.3s ease;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  display: inline-block;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const PaginationDots = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 30px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    bottom: 60px;
    gap: 8px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  z-index: 11;
  padding: 0 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;

  &:hover {
    background: rgba(0, 0, 0, 0.65);
    transform: scale(1.1);
    
    img {
      filter: brightness(0) invert(1);
    }
  }

  img {
    width: 20px;
    height: 20px;
    transition: filter 0.3s ease;
    filter: brightness(0) invert(1);
  }
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${props => props.$active ? '12px' : '10px'};
  height: ${props => props.$active ? '12px' : '10px'};
  border-radius: 50%;
  border: 2px solid ${theme.colors.white};
  background: ${props => props.$active ? theme.colors.white : 'transparent'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  outline: none;

  &:hover {
    transform: scale(1.2);
    background: ${props => props.$active ? theme.colors.white : 'rgba(255, 255, 255, 0.5)'};
  }

  &:focus {
    outline: 2px solid ${theme.colors.white};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: ${props => props.$active ? '10px' : '8px'};
    height: ${props => props.$active ? '10px' : '8px'};
    border-width: 1.5px;
  }

  @media (max-width: 480px) {
    width: ${props => props.$active ? '8px' : '6px'};
    height: ${props => props.$active ? '8px' : '6px'};
    border-width: 1px;
  }
`;

interface HeroSlide {
  type: 'video' | 'page';
  videoSrc?: string;
  image?: string;
  title: string;
  text: string;
  linkPath?: string;
}

// Créer les slides : vidéo d'intro + toutes les pages de navigation (sauf accueil) + marketplace
// L'ordre suit l'ordre alphabétique de navigationItems + marketplace à la fin
const createSlides = (): HeroSlide[] => [
  {
    type: 'video',
    videoSrc: '/shared_assets/videos/accueil.mp4',
    title: 'Accueil',
    text: 'Bienvenue sur Outchou, votre espace de référence dédié à la richesse et à la diversité de la culture algérienne. Ce site rassemble l\'héritage d\'un pays aux multiples visages : traditions vestimentaires, savoir-faire artisanaux, gastronomie variée, musiques et danses populaires, mais aussi paysages contrastés allant de la Méditerranée au Sahara.'
  },
  ...navigationItems
    .filter(item => item.id !== 'accueil' && item.id !== 'marketplace') // Exclure accueil et marketplace
    .map(item => ({
      type: 'page' as const,
      image: `/shared_assets/images/navigation/${item.id}.jpeg`,
      title: item.label,
      text: item.page_text,
      linkPath: item.path
    })),
  // Ajouter la Marketplace à la fin
  ...navigationItems
    .filter(item => item.id === 'marketplace')
    .map(item => ({
      type: 'page' as const,
      image: `/shared_assets/images/navigation/${item.id}.jpeg`,
      title: item.label,
      text: item.page_text,
      linkPath: item.path
    }))
];

// Fonction pour extraire la première phrase d'un texte
const getFirstSentence = (text: string): string => {
  // Trouver la première phrase (se termine par . ! ? ou fin de texte)
  const match = text.match(/^[^.!?]+[.!?]/);
  if (match) {
    return match[0].trim();
  }
  // Si pas de ponctuation, retourner les premiers mots (environ 20 mots max)
  const words = text.split(' ');
  if (words.length > 20) {
    return words.slice(0, 20).join(' ') + '...';
  }
  return text;
};

const RotatingHero: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const slides = React.useMemo(() => createSlides(), []);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Détecter les interactions utilisateur pour permettre le démarrage de la vidéo
  useEffect(() => {
    // Ne pas marquer comme interagi au premier chargement
    if (isInitialMount) {
      return;
    }

    const handleUserInteraction = () => {
      setHasUserInteracted(true);
    };

    // Écouter plusieurs types d'interactions
    window.addEventListener('click', handleUserInteraction, { once: true });
    window.addEventListener('touchstart', handleUserInteraction, { once: true });
    window.addEventListener('keydown', handleUserInteraction, { once: true });
    window.addEventListener('scroll', handleUserInteraction, { once: true });

    return () => {
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('scroll', handleUserInteraction);
    };
  }, [isInitialMount]);

  // Détecter le premier montage du composant
  useEffect(() => {
    // Ne jamais marquer comme non-initial au premier chargement de la page
    // On utilise sessionStorage pour détecter si c'est vraiment le premier chargement
    const isFirstVisit = !sessionStorage.getItem('outchou_visited');
    if (isFirstVisit) {
      sessionStorage.setItem('outchou_visited', 'true');
      // Rester en mode initial mount pour le premier chargement
      return;
    }
    // Si ce n'est pas le premier chargement, marquer comme non-initial après un délai
    const timer = setTimeout(() => {
      setIsInitialMount(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Redémarrer la vidéo quand on revient sur la page d'accueil (mais pas au premier chargement)
  useEffect(() => {
    // Ne démarrer la vidéo que si l'utilisateur a interagi ET si ce n'est pas le premier chargement
    if (location.pathname === '/' && hasUserInteracted && !isInitialMount) {
      // Réinitialiser tous les états
      setCurrentIndex(0);
      setShowLogo(false);
      setVideoEnded(false);
      setIsVideoPlaying(false);
      
      // Redémarrer la vidéo après un court délai pour s'assurer que le composant est monté
      const restartTimer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(() => {
            // Si la vidéo ne peut pas être jouée, ignorer
          });
        }
      }, 200);
      
      return () => clearTimeout(restartTimer);
    }
  }, [location.pathname, slides.length, isInitialMount, hasUserInteracted]);

  useEffect(() => {
    const currentSlide = slides[currentIndex];
    let timer: NodeJS.Timeout | null = null;
    let logoTimer: NodeJS.Timeout | null = null;
    
    if (currentSlide.type === 'video') {
      // Pour la vidéo, on attend qu'elle se termine puis on attend 7 secondes
      if (videoRef.current) {
        const video = videoRef.current;
        
        const handleEnded = () => {
          setVideoEnded(true);
          setIsVideoPlaying(false);
          // Afficher le logo Outchou quand la vidéo se termine
          setShowLogo(true);
          
          // Ne pas masquer le logo - il reste visible
          
          // Attendre 7 secondes après la fin de la vidéo
          timer = setTimeout(() => {
            // Préparer le prochain index avant la transition
            const newIndex = (currentIndex + 1) % slides.length;
            setNextIndex(newIndex);
            // Petit délai pour s'assurer que le slide suivant est rendu
            setTimeout(() => {
              setCurrentIndex(newIndex);
              setNextIndex(null);
              setVideoEnded(false);
              // Masquer le logo quand on passe au slide suivant
              setShowLogo(false);
            }, 50);
          }, 7000);
        };

        const handlePlay = () => {
          setIsVideoPlaying(true);
          // Masquer le logo quand la vidéo joue (au début ou après redémarrage)
          setShowLogo(false);
        };

        video.addEventListener('ended', handleEnded);
        video.addEventListener('play', handlePlay);

        // Ne pas démarrer la vidéo automatiquement au premier chargement de la page
        // La vidéo ne démarrera que si l'utilisateur interagit avec la page ET si ce n'est pas le premier chargement
        if (hasUserInteracted && !isInitialMount) {
          // Essayer de jouer la vidéo seulement si l'utilisateur a interagi ET si ce n'est pas le premier chargement
          video.play().catch(() => {
            // Si la vidéo ne peut pas être jouée (fichier manquant), passer au suivant après un délai
            timer = setTimeout(() => {
              const newIndex = (currentIndex + 1) % slides.length;
              setNextIndex(newIndex);
              setTimeout(() => {
                setCurrentIndex(newIndex);
                setNextIndex(null);
                setShowLogo(false);
              }, 50);
            }, 3000);
          });
        } else {
          // Au premier chargement, ne pas démarrer la vidéo automatiquement
          // Elle démarrera quand l'utilisateur interagira avec la page ou quand on reviendra sur la page
        }

        return () => {
          video.removeEventListener('ended', handleEnded);
          video.removeEventListener('play', handlePlay);
          if (timer) clearTimeout(timer);
          if (logoTimer) clearTimeout(logoTimer);
        };
      }
    } else {
      // Pour les pages avec images, changer après 8 secondes
      timer = setTimeout(() => {
        const newIndex = (currentIndex + 1) % slides.length;
        setNextIndex(newIndex);
        setTimeout(() => {
          setCurrentIndex(newIndex);
          setNextIndex(null);
        }, 50);
      }, 8000);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [currentIndex, slides.length]);

  const handleDiscoverClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    navigate(path);
  };

  const handleSlideClick = (e: React.MouseEvent, slide: typeof slides[0]) => {
    // Sur mobile, on utilise le swipe, pas le clic
    // Le clic ne fait rien sur mobile
  };

  const handleDotClick = (index: number) => {
    if (index !== currentIndex) {
      setNextIndex(index);
      setTimeout(() => {
        setCurrentIndex(index);
        setNextIndex(null);
        setShowLogo(false);
        setVideoEnded(false);
        setIsVideoPlaying(false);
      }, 50);
    }
  };

  const goToIndex = (index: number) => {
    if (index === currentIndex) return;
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch {}
    }
    setNextIndex(index);
    setTimeout(() => {
      setCurrentIndex(index);
      setNextIndex(null);
      setShowLogo(false);
      setVideoEnded(false);
      setIsVideoPlaying(false);
    }, 50);
  };

  const goPrev = () => {
    const index = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
    goToIndex(index);
  };

  const goNext = () => {
    const index = (currentIndex + 1) % slides.length;
    goToIndex(index);
  };

  // Gestion du swipe sur mobile avec glissement visuel
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || touchStart === null) return;
    const currentX = e.targetTouches[0].clientX;
    setTouchEnd(currentX);
    // Calculer le décalage en pixels pour le glissement visuel
    const offset = currentX - touchStart;
    setDragOffset(offset);
  };

  const onTouchEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) {
      setIsDragging(false);
      setDragOffset(0);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    setIsDragging(false);
    setDragOffset(0);

    if (isLeftSwipe) {
      // Swipe gauche : slide suivant
      const newIndex = (currentIndex + 1) % slides.length;
      setNextIndex(newIndex);
      setTimeout(() => {
        setCurrentIndex(newIndex);
        setNextIndex(null);
        setShowLogo(false);
        setVideoEnded(false);
        setIsVideoPlaying(false);
      }, 50);
    } else if (isRightSwipe) {
      // Swipe droite : slide précédent
      const newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
      setNextIndex(newIndex);
      setTimeout(() => {
        setCurrentIndex(newIndex);
        setNextIndex(null);
        setShowLogo(false);
        setVideoEnded(false);
        setIsVideoPlaying(false);
      }, 50);
    }
  };

  // Variantes d'animation pour Framer Motion - toujours de droite à gauche
  const slideVariants = {
    enter: {
      x: '100%',
      opacity: 1
    },
    center: {
      x: 0,
      opacity: 1
    },
    exit: {
      x: '-100%',
      opacity: 1
    }
  };

  const transition = {
    duration: 0.8,
    ease: [0.4, 0, 0.2, 1] as const
  };

  // Ce useEffect a été déplacé plus haut pour être utilisé correctement

  const currentSlide = slides[currentIndex];
  const nextSlide = nextIndex !== null ? slides[nextIndex] : null;

  return (
    <HeroSection>
      <AnimatePresence mode="sync" initial={false}>
        {/* Slide actuel */}
        <SlideContainer
          key={currentIndex}
          variants={slideVariants}
          initial={isInitialMount ? "center" : "enter"}
          animate="center"
          exit="exit"
          transition={isDragging ? { duration: 0 } : transition}
          style={{ 
            zIndex: nextIndex !== null ? 1 : 2,
            cursor: isMobile && currentSlide.type === 'page' && currentSlide.linkPath ? 'pointer' : 'default',
            transform: isDragging && isMobile ? `translateX(${dragOffset}px)` : undefined
          }}
          $backgroundImage={currentSlide.type === 'page' ? currentSlide.image : undefined}
          onClick={(e) => handleSlideClick(e, currentSlide)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {currentSlide.type === 'video' && currentSlide.videoSrc && (
            <>
              <VideoElement
                ref={videoRef}
                src={currentSlide.videoSrc}
                playsInline
                muted
                autoPlay
                preload="auto"
              />
              <HeroOverlay />
            </>
          )}
          {currentSlide.type === 'page' && <HeroOverlay />}
          
            <HeroContent>
              <ClickableTextArea 
                $isMobile={isMobile}
                onClick={() => {
                  if (isMobile && currentSlide.linkPath) {
                    navigate(currentSlide.linkPath);
                  }
                }}
              >
                <TitleContainer>
                  <HeroTitle>{currentSlide.title}</HeroTitle>
                </TitleContainer>
                <HeroText>{isMobile ? getFirstSentence(currentSlide.text) : currentSlide.text}</HeroText>
              </ClickableTextArea>
            {currentSlide.linkPath && (
              <DiscoverLink 
                href={currentSlide.linkPath}
                onClick={(e) => handleDiscoverClick(e, currentSlide.linkPath!)}
              >
                Découvrir
              </DiscoverLink>
            )}
          </HeroContent>
        </SlideContainer>

        {/* Slide suivant (préparé pour la transition) */}
        {nextSlide && (
          <SlideContainer
            key={`next-${nextIndex}`}
            variants={slideVariants}
            initial="enter"
            animate="center"
            transition={transition}
            style={{ 
              zIndex: 2,
              cursor: isMobile && nextSlide.type === 'page' && nextSlide.linkPath ? 'pointer' : 'default'
            }}
            $backgroundImage={nextSlide.type === 'page' ? nextSlide.image : undefined}
            onClick={(e) => handleSlideClick(e, nextSlide)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {nextSlide.type === 'video' && nextSlide.videoSrc && (
              <>
                <VideoElement
                  src={nextSlide.videoSrc}
                  playsInline
                  muted
                  preload="auto"
                />
                <HeroOverlay />
              </>
            )}
            {nextSlide.type === 'page' && <HeroOverlay />}
            
            <HeroContent>
              <ClickableTextArea 
                $isMobile={isMobile}
                onClick={() => {
                  if (isMobile && nextSlide.linkPath) {
                    navigate(nextSlide.linkPath);
                  }
                }}
              >
                <TitleContainer>
                  <HeroTitle>{nextSlide.title}</HeroTitle>
                </TitleContainer>
                <HeroText>{isMobile ? getFirstSentence(nextSlide.text) : nextSlide.text}</HeroText>
              </ClickableTextArea>
              {nextSlide.linkPath && (
                <DiscoverLink 
                  href={nextSlide.linkPath}
                  onClick={(e) => handleDiscoverClick(e, nextSlide.linkPath!)}
                >
                  Découvrir
                </DiscoverLink>
              )}
            </HeroContent>
          </SlideContainer>
        )}
      </AnimatePresence>

      {/* Flèche double animée en bas sur mobile */}
      <ScrollIndicator
        onClick={() => {
          const banner = document.getElementById('outchou-banner');
          if (banner) {
            banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
      >
        <DoubleArrow />
      </ScrollIndicator>

      {/* Logo Outchou qui apparaît à la fin de la vidéo */}
      {currentSlide.type === 'video' && (
        <LogoAnimation $show={showLogo}>
          <LogoWhite />
        </LogoAnimation>
      )}

      {/* Flèches de navigation (desktop) */}
      <CarouselControls>
        <CarouselButton onClick={goPrev} aria-label="Slide précédente">
          <img src="/shared_assets/images/carousel_left.png" alt="Previous" />
        </CarouselButton>
        <CarouselButton onClick={goNext} aria-label="Slide suivante">
          <img src="/shared_assets/images/carousel_right.png" alt="Next" />
        </CarouselButton>
      </CarouselControls>

      {/* Points de pagination */}
      <PaginationDots>
        {slides.map((_, index) => (
          <Dot
            key={index}
            $active={index === currentIndex}
            onClick={() => handleDotClick(index)}
            aria-label={`Aller à la slide ${index + 1}`}
          />
        ))}
      </PaginationDots>
    </HeroSection>
  );
};

export default RotatingHero;

